import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BarChart3, Download, ChevronRight, AlertCircle, RotateCcw, Search, Users, Building2, EyeOff, Eye, CheckCircle2, Sparkles, TrendingUp, Target, ListChecks, Lightbulb, Award } from "lucide-react";
import { ScreenShell, GlassCard, GradientText, SimHeader } from "@/components/sim/shared";
import { AdminNav } from "@/components/neusim/AdminNav";
import { apiCall, apiDownload, ApiError } from "@/lib/api";
import { generateCohortReportPdf, downloadBlob, slugForFile, type CohortReportData } from "@/lib/simPdf";

interface CohortRow {
  cohortName: string;
  simulationId: string;
  simCode: string;
  simTitle: string;
  projectId: string;
  projectName: string;
  clientId: string;
  clientName: string;
  assigned: number;
  started: number;
  completed: number;
  timedOut: number;
  avgScore: number | null;
  bestScore: number | null;
  strongCount: number;
  developingCount: number;
  needsCount: number;
  lastActivity: string | null;
}

interface LearnerRow {
  userId: string;
  email: string;
  fullName: string;
  assignedAt: string;
  attemptCount: number;
  latestStatus: string | null;
  latestStartedAt: string | null;
  latestEndedAt: string | null;
  bestScore: number | null;
  bestBand: string | null;
  bestXp: number | null;
  bestDecisions: number | null;
}

interface ClientOpt { id: string; name: string; isActive: boolean }

/**
 * Mirror of api/shared CohortInsights — 6 prose fields per the upgraded
 * Power-App-style prompt. `recommendations` and `decisionDeepDives` arrive
 * as " || "-joined strings the panel splits for display.
 */
interface CohortInsights {
  strengths: string;
  development: string;
  themes: string;
  recommendations: string;
  readiness: string;
  decisionDeepDives: string;
}

interface InsightsState {
  loading: boolean;
  data: CohortInsights | null;
  cached: boolean;
  generatedAt: string | null;
  cohortSize: number | null;
  error: string | null;
}

function fmtRelative(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function pct(num: number, den: number): string {
  if (den <= 0) return "0%";
  return `${Math.round((num / den) * 100)}%`;
}

function statusToken(status: string | null): { color: string; label: string } {
  switch (status) {
    case "completed": return { color: "#10b981", label: "Completed" };
    case "in_progress": return { color: "#f59e0b", label: "In progress" };
    case "timed_out": return { color: "#f87272", label: "Timed out" };
    default: return { color: "#A78BFA", label: "Not started" };
  }
}

/**
 * AdminAnalyticsPage — /admin/analytics — Step 7 v1.
 *
 * One row per (cohort × sim) pair with completion + score aggregates.
 * Expand a row to drill into per-learner detail, with privacy toggle
 * (anonymous mode swaps real names for "Learner #N"). Per-row CSV
 * download.
 *
 * v1 = data + table. v2 will add Claude-powered AI insights (cached in
 * cohort_insights table). v3 will add a PDF cohort report.
 *
 * Super_admin only — enforced server-side.
 */
export function AdminAnalyticsPage(): JSX.Element {
  const navigate = useNavigate();

  const [rows, setRows] = useState<CohortRow[] | null>(null);
  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [clientFilter, setClientFilter] = useState<string>("");
  const [query, setQuery] = useState<string>("");

  const [openKey, setOpenKey] = useState<string | null>(null);
  const [detailsByKey, setDetailsByKey] = useState<Record<string, LearnerRow[]>>({});
  const [detailLoading, setDetailLoading] = useState<string | null>(null);
  const [anonymous, setAnonymous] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [insightsByKey, setInsightsByKey] = useState<Record<string, InsightsState>>({});

  const loadRows = async (): Promise<void> => {
    setLoading(true); setError(null);
    try {
      // Two-tier resilience: 120s timeout (some Azure Functions cold starts
      // run 40-50s before the worker is even ready), plus one silent retry
      // on abort. After a cold start the second call is hot and instant.
      const fetchOnce = (): Promise<{ rows: CohortRow[] }> => apiCall<{ rows: CohortRow[] }>("/libraryCohorts", {
        method: "POST",
        body: { clientId: clientFilter === "" ? undefined : clientFilter },
        timeoutMs: 120_000,
      });
      let res: { rows: CohortRow[] };
      try {
        res = await fetchOnce();
      } catch (e) {
        const isAbort = e instanceof Error && /abort/i.test(e.message);
        if (!isAbort) throw e;
        // Wait 1s for the worker to settle then retry once.
        await new Promise((r) => setTimeout(r, 1000));
        res = await fetchOnce();
      }
      setRows(res.rows);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't load cohort data");
    } finally { setLoading(false); }
  };

  const loadClients = async (): Promise<void> => {
    try {
      const res = await apiCall<{ clients: ClientOpt[] }>("/libraryClients");
      setClients(res.clients);
    } catch { /* keep empty picker */ }
  };

  useEffect(() => { void loadClients(); }, []);
  useEffect(() => { void loadRows(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [clientFilter]);

  const filtered = useMemo(() => {
    if (rows === null) return [];
    if (query.trim() === "") return rows;
    const q = query.toLowerCase();
    return rows.filter((r) =>
      r.cohortName.toLowerCase().includes(q) ||
      r.simTitle.toLowerCase().includes(q) ||
      r.simCode.toLowerCase().includes(q) ||
      r.clientName.toLowerCase().includes(q),
    );
  }, [rows, query]);

  const totals = useMemo(() => {
    if (rows === null) return { cohorts: 0, sims: 0, learners: 0, completions: 0 };
    const cohortSet = new Set(rows.map((r) => `${r.clientId}|${r.cohortName}`));
    const simSet = new Set(rows.map((r) => r.simulationId));
    return {
      cohorts: cohortSet.size,
      sims: simSet.size,
      learners: rows.reduce((s, r) => s + r.assigned, 0),
      completions: rows.reduce((s, r) => s + r.completed, 0),
    };
  }, [rows]);

  const keyOf = (r: CohortRow): string => `${r.simulationId}|${r.cohortName}`;

  const loadInsights = async (r: CohortRow, regenerate: boolean): Promise<void> => {
    const k = keyOf(r);
    setInsightsByKey((prev) => ({ ...prev, [k]: { ...(prev[k] ?? { data: null, cached: false, generatedAt: null, cohortSize: null }), loading: true, error: null } }));
    try {
      const res = await apiCall<{ insights: CohortInsights; cached: boolean; generatedAt: string; cohortSize: number }>(
        "/libraryCohortInsights",
        {
          method: "POST",
          body: { simulationId: r.simulationId, cohortName: r.cohortName, regenerate },
          // Fresh generation now reads full per-learner transcripts (~35K input
          // tokens) and emits 6 prose fields (~2.5K output). 60-120s typical;
          // 150s gives headroom. Cached path is instant.
          timeoutMs: 150_000,
        },
      );
      setInsightsByKey((prev) => ({
        ...prev,
        [k]: { loading: false, data: res.insights, cached: res.cached, generatedAt: res.generatedAt, cohortSize: res.cohortSize, error: null },
      }));
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't generate insights";
      setInsightsByKey((prev) => ({
        ...prev,
        [k]: { ...(prev[k] ?? { data: null, cached: false, generatedAt: null, cohortSize: null }), loading: false, error: msg },
      }));
    }
  };

  const toggleExpand = async (r: CohortRow): Promise<void> => {
    const k = keyOf(r);
    if (openKey === k) { setOpenKey(null); return; }
    setOpenKey(k);
    // Try to load cached insights on expand (regenerate=false). A 502 / no-cache
    // simply leaves state empty; the panel shows a "Generate" CTA in that case.
    if (insightsByKey[k]?.data === undefined && insightsByKey[k]?.loading !== true) {
      void loadInsights(r, false);
    }
    if (detailsByKey[k] !== undefined) return;
    setDetailLoading(k);
    try {
      const res = await apiCall<{ learners: LearnerRow[] }>("/libraryCohortDetail", {
        method: "POST",
        body: { simulationId: r.simulationId, cohortName: r.cohortName, anonymous },
        timeoutMs: 60_000,
      });
      setDetailsByKey((prev) => ({ ...prev, [k]: res.learners }));
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't load learners";
      setToast(`Error: ${msg}`);
      setTimeout(() => setToast(null), 3000);
    } finally { setDetailLoading(null); }
  };

  // When the anonymous toggle flips, re-fetch any open detail so the labels
  // update without a manual collapse/reopen.
  useEffect(() => {
    if (openKey === null) return;
    const [simulationId, cohortName] = openKey.split("|");
    if (simulationId === undefined || cohortName === undefined) return;
    void (async () => {
      setDetailLoading(openKey);
      try {
        const res = await apiCall<{ learners: LearnerRow[] }>("/libraryCohortDetail", {
          method: "POST",
          body: { simulationId, cohortName, anonymous },
          timeoutMs: 60_000,
        });
        setDetailsByKey((prev) => ({ ...prev, [openKey]: res.learners }));
      } catch { /* leave stale */ } finally { setDetailLoading(null); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anonymous]);

  const handleExport = async (r: CohortRow): Promise<void> => {
    try {
      const safeCohort = r.cohortName.replace(/[^a-z0-9]+/gi, "-");
      await apiDownload("/libraryCohortExport", `NeuSim-${r.simCode}-${safeCohort}.csv`, {
        method: "POST",
        body: { simulationId: r.simulationId, cohortName: r.cohortName, anonymous },
      });
      setToast(`Downloaded ${r.simCode} · ${r.cohortName}`);
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Export failed";
      setToast(`Error: ${msg}`);
    }
  };

  const handlePdf = async (r: CohortRow): Promise<void> => {
    try {
      const data = await apiCall<CohortReportData>("/libraryCohortReport", {
        method: "POST",
        body: { simulationId: r.simulationId, cohortName: r.cohortName, anonymous },
        timeoutMs: 60_000,
      });
      const blob = await generateCohortReportPdf(data);
      downloadBlob(blob, `NeuSim-Cohort-${slugForFile(r.simCode)}-${slugForFile(r.cohortName)}.pdf`);
      setToast(`Downloaded PDF · ${r.simCode} · ${r.cohortName}`);
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "PDF generation failed";
      setToast(`Error: ${msg}`);
    }
  };

  return (
    <ScreenShell>
      <SimHeader decision={totals.cohorts} xp={0} />
      <main className="mx-auto max-w-7xl px-4 pb-20 md:px-8">
        <div className="mb-5"><AdminNav /></div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-fuchsia-300">
                <BarChart3 className="h-4 w-4" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em]">Cohort Analytics</span>
              </div>
              <h1 className="mt-2 text-[28px] font-bold leading-tight">
                <GradientText>Live cohort dashboard</GradientText>
              </h1>
              <p className="mt-1 text-sm text-white/55">One row per (cohort × sim). Expand a row to drill into per-learner detail. Use anonymous mode when sharing screens with the client.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setAnonymous((p) => !p)}
                title="Toggle anonymous learner labels"
                className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-[12px] font-medium transition-colors ${anonymous ? "border-cyan-400/40 bg-cyan-500/15 text-cyan-200" : "border-white/15 bg-white/5 text-white/70"}`}
              >
                {anonymous ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {anonymous ? "Anonymous" : "Real names"}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Summary tiles */}
        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Cohorts" value={totals.cohorts} icon={<Users className="h-3.5 w-3.5 text-fuchsia-300" />} />
          <Stat label="Sims in flight" value={totals.sims} icon={<BarChart3 className="h-3.5 w-3.5 text-cyan-300" />} />
          <Stat label="Learners assigned" value={totals.learners} icon={<Users className="h-3.5 w-3.5 text-violet-300" />} />
          <Stat label="Completions" value={totals.completions} icon={<CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />} />
        </div>

        {/* Filters */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/35" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search cohort / sim / client"
              className="w-72 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 pl-8 text-[12px] text-white placeholder:text-white/30 outline-none focus:border-fuchsia-400/60"
            />
          </div>
          <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className="rounded-lg border border-white/15 bg-[#0c0e22] px-3 py-1.5 text-[12px] text-white outline-none focus:border-fuchsia-400/60">
            <option value="">All clients</option>
            {clients.filter((c) => c.isActive).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {toast !== null && (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-1.5 text-[11px] text-emerald-200">
            <CheckCircle2 className="h-3.5 w-3.5" /> {toast}
          </div>
        )}

        {loading && <p className="mt-6 text-sm text-white/45">Loading cohort data…</p>}
        {!loading && error !== null && (
          <GlassCard className="mx-auto mt-8 max-w-lg p-8 text-center">
            <AlertCircle className="mx-auto h-9 w-9 text-[#f87272]" />
            <p className="mt-3 text-sm text-white/70">{error}</p>
            <button type="button" onClick={() => void loadRows()} className="mt-5 inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white hover:border-white/30">
              <RotateCcw className="h-3.5 w-3.5" /> Try again
            </button>
          </GlassCard>
        )}

        {!loading && error === null && filtered.length === 0 && (
          <GlassCard className="mt-6 p-10 text-center">
            <p className="text-sm text-white/55">No cohort rows yet. Assign sims to learners on /admin/users to populate.</p>
          </GlassCard>
        )}

        {!loading && error === null && filtered.length > 0 && (
          <GlassCard className="mt-6 overflow-hidden p-0">
            <table className="w-full table-fixed text-[12.5px]">
              <colgroup>
                <col style={{ width: "2.5rem" }} />
                <col />
                <col style={{ width: "9rem" }} />
                <col style={{ width: "4.5rem" }} />
                <col style={{ width: "5rem" }} />
                <col style={{ width: "5rem" }} />
                <col style={{ width: "4.5rem" }} />
                <col style={{ width: "9rem" }} />
                <col style={{ width: "5.5rem" }} />
              </colgroup>
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.02] text-[10px] uppercase tracking-[0.12em] text-white/40">
                  <th></th>
                  <th className="px-3 py-2.5 text-left">Cohort · Sim</th>
                  <th className="px-3 py-2.5 text-left">Client</th>
                  <th className="px-3 py-2.5 text-right">Assigned</th>
                  <th className="px-3 py-2.5 text-right">Started</th>
                  <th className="px-3 py-2.5 text-right">Completed</th>
                  <th className="px-3 py-2.5 text-right">Avg</th>
                  <th className="px-3 py-2.5 text-left">Band mix</th>
                  <th className="px-3 py-2.5 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const k = keyOf(r);
                  const isOpen = openKey === k;
                  const details = detailsByKey[k] ?? null;
                  return (
                    <RowGroup key={k}>
                      <tr
                        onClick={() => void toggleExpand(r)}
                        className="cursor-pointer border-b border-white/5 hover:bg-white/[0.03]"
                      >
                        <td className="px-1 py-2.5 text-center text-white/40">
                          <ChevronRight className={`inline h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                        </td>
                        <td className="px-3 py-2.5 truncate">
                          <div className="flex items-center gap-2">
                            <span className="rounded-full border border-cyan-400/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-semibold text-cyan-200">{r.cohortName}</span>
                            <span className="truncate text-white">
                              <span className="font-mono text-[10px] text-white/40">{r.simCode}</span> · {r.simTitle}
                            </span>
                          </div>
                          <div className="mt-0.5 text-[10.5px] text-white/40 truncate">{r.projectName}</div>
                        </td>
                        <td className="px-3 py-2.5 truncate text-white/65 text-[11.5px]">
                          <Building2 className="mr-1 inline h-3 w-3 text-white/35" />{r.clientName}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-white/80">{r.assigned}</td>
                        <td className="px-3 py-2.5 text-right">
                          <span className="font-mono text-white/80">{r.started}</span>
                          <span className="ml-1 text-[10px] text-white/40">{pct(r.started, r.assigned)}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <span className="font-mono text-emerald-200/85">{r.completed}</span>
                          <span className="ml-1 text-[10px] text-white/40">{pct(r.completed, r.assigned)}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-white/80">{r.avgScore !== null ? r.avgScore.toFixed(1) : "—"}</td>
                        <td className="px-3 py-2.5">
                          <BandBar strong={r.strongCount} developing={r.developingCount} needs={r.needsCount} />
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); void handleExport(r); }}
                              className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[10.5px] text-white/75 hover:border-white/30"
                              title="Download CSV"
                              aria-label="Download CSV"
                            >
                              <Download className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); void handlePdf(r); }}
                              className="inline-flex items-center gap-1 rounded-md border border-violet-400/30 bg-violet-500/10 px-2 py-1 text-[10.5px] font-semibold text-violet-200 hover:border-violet-400/60 hover:bg-violet-500/15"
                              title="Download branded cohort PDF (includes AI insights if generated)"
                              aria-label="Download cohort PDF"
                            >
                              PDF
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-white/5 bg-white/[0.02]">
                          <td></td>
                          <td colSpan={8} className="px-3 py-3">
                            <InsightsPanel
                              state={insightsByKey[k]}
                              onGenerate={() => void loadInsights(r, false)}
                              onRegenerate={() => void loadInsights(r, true)}
                            />
                            <h3 className="mb-2 mt-5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-cyan-200">
                              <Users className="mr-1 inline h-3 w-3" /> Learners in {r.cohortName}
                            </h3>
                            {detailLoading === k && <p className="text-[11px] text-white/45">Loading…</p>}
                            {detailLoading !== k && details !== null && details.length === 0 && (
                              <p className="text-[11px] text-white/45">No learners assigned in this cohort.</p>
                            )}
                            {detailLoading !== k && details !== null && details.length > 0 && (
                              <div className="space-y-1.5">
                                {details.map((l) => {
                                  const tok = statusToken(l.latestStatus);
                                  return (
                                    <div key={l.userId} className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 rounded-md border border-white/8 bg-white/[0.02] px-3 py-2">
                                      <div className="min-w-0 truncate">
                                        <div className="truncate text-[12px] font-medium text-white">{l.fullName}</div>
                                        <div className="truncate text-[10.5px] text-white/45">{l.email}</div>
                                      </div>
                                      <span
                                        className="rounded-full border px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.1em]"
                                        style={{ background: `${tok.color}20`, borderColor: `${tok.color}66`, color: tok.color }}
                                      >
                                        {tok.label}
                                      </span>
                                      <span className="font-mono text-[11px] text-white/65">{l.attemptCount} att</span>
                                      <span className="font-mono text-[11px] text-white/80">
                                        {l.bestScore !== null ? `Best ${l.bestScore}` : "—"}
                                        {l.bestBand !== null ? ` · ${l.bestBand}` : ""}
                                      </span>
                                      <span className="text-[10.5px] text-white/45">{fmtRelative(l.latestEndedAt ?? l.latestStartedAt)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </RowGroup>
                  );
                })}
              </tbody>
            </table>
          </GlassCard>
        )}
      </main>
    </ScreenShell>
  );
}

function RowGroup({ children }: { children: React.ReactNode }): JSX.Element { return <>{children}</>; }

/**
 * Split a "||"-joined recommendations / deep-dive string into the individual
 * sentence cards. Tolerates trailing whitespace and missing separators.
 */
function splitItems(joined: string): string[] {
  return joined
    .split(/\s*\|\|\s*/g)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Pull the closing "Ready / Ready with X / Needs Y before ready" verdict out
 * of the readiness paragraph for a visual call-out. Falls back to "Verdict"
 * if Claude didn't end the paragraph with one of the expected phrases.
 */
function extractReadinessCall(text: string): { body: string; call: string | null } {
  const trimmed = text.trim();
  const m = trimmed.match(/(Ready(?:\s+with\s+[^.]+)?\.?|Needs\s+[^.]+\sbefore\s+ready\.?)\s*$/i);
  if (m === null) return { body: trimmed, call: null };
  const call = m[1]!.trim().replace(/\.+$/, "");
  const body = trimmed.slice(0, trimmed.length - m[0].length).trim();
  return { body, call };
}

function InsightsPanel({ state, onGenerate, onRegenerate }: { state: InsightsState | undefined; onGenerate: () => void; onRegenerate: () => void }): JSX.Element {
  const data = state?.data ?? null;
  const loading = state?.loading === true;
  const cached = state?.cached === true;
  const error = state?.error ?? null;
  const generatedAt = state?.generatedAt ?? null;
  const cohortSize = state?.cohortSize ?? null;

  return (
    <div className="rounded-xl border border-fuchsia-400/20 bg-gradient-to-br from-fuchsia-500/[0.06] via-violet-500/[0.03] to-cyan-400/[0.03] p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-fuchsia-200">
          <Sparkles className="h-3 w-3" /> Cohort insights
          {cached && generatedAt !== null && (
            <span className="ml-2 normal-case tracking-normal text-white/35">
              cached · generated {new Date(generatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
              {cohortSize !== null ? ` for ${cohortSize} learner${cohortSize === 1 ? "" : "s"}` : ""}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {data === null && !loading && error === null && (
            <button
              type="button"
              onClick={onGenerate}
              className="inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/15 px-3 py-1.5 text-[11px] font-semibold text-fuchsia-100 hover:border-fuchsia-400/60 hover:bg-fuchsia-500/25"
            >
              <Sparkles className="h-3 w-3" /> Generate insights
            </button>
          )}
          {data !== null && !loading && (
            <button
              type="button"
              onClick={onRegenerate}
              className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[10.5px] text-white/75 hover:border-white/30"
              title="Re-run Claude with the current cohort data"
            >
              <RotateCcw className="h-3 w-3" /> Regenerate
            </button>
          )}
        </div>
      </div>

      {loading && (
        <p className="mt-3 flex items-center gap-1.5 text-[11.5px] text-white/55">
          <Sparkles className="h-3 w-3 animate-pulse text-fuchsia-300" /> Generating insights — Claude is reading every learner&rsquo;s transcript. 60–120s.
        </p>
      )}

      {error !== null && !loading && (
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-rose-400/40 bg-rose-500/10 px-3 py-1.5 text-[11px] text-rose-200">
          <AlertCircle className="h-3 w-3" /> {error}
        </div>
      )}

      {data === null && !loading && error === null && (
        <p className="mt-2 text-[11.5px] text-white/45">
          Click <span className="font-semibold text-fuchsia-200">Generate insights</span>. Claude reads every learner&rsquo;s actual answers + per-decision evaluator feedback (names &amp; emails are stripped) and returns a 6-section debrief brief: strengths, development gaps, cross-cohort themes, three intervention recommendations, a readiness verdict, and decision-by-decision deep-dives.
        </p>
      )}

      {data !== null && !loading && (() => {
        const readiness = extractReadinessCall(data.readiness);
        return (
          <div className="mt-4 space-y-4">
            {/* Readiness verdict — the standout. */}
            <div className="rounded-xl border border-violet-400/30 bg-gradient-to-br from-violet-500/15 to-cyan-500/10 p-4">
              <div className="flex items-start gap-3">
                <Award className="mt-0.5 h-4 w-4 shrink-0 text-violet-200" />
                <div className="flex-1">
                  <h4 className="text-[10px] font-bold uppercase tracking-[0.14em] text-violet-200">Readiness verdict</h4>
                  <p className="mt-1.5 text-[13.5px] leading-relaxed text-white/85">{readiness.body}</p>
                  {readiness.call !== null && (
                    <p className="mt-2 inline-flex items-center rounded-full border border-violet-400/40 bg-violet-500/20 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.1em] text-violet-100">
                      {readiness.call}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <ProseSection title="Strengths"          body={data.strengths}   color="#10b981" icon={<TrendingUp className="h-3 w-3" />} />
              <ProseSection title="Development gaps"   body={data.development} color="#f59e0b" icon={<Target className="h-3 w-3" />} />
              <ProseSection title="Cross-cohort themes" body={data.themes}     color="#22d3ee" icon={<ListChecks className="h-3 w-3" />} />
              <NumberedSection title="Recommended interventions" items={splitItems(data.recommendations)} color="#A78BFA" icon={<Lightbulb className="h-3 w-3" />} />
            </div>

            <NumberedSection title="Decision deep-dives" items={splitItems(data.decisionDeepDives)} color="#F472B6" icon={<Sparkles className="h-3 w-3" />} full />
          </div>
        );
      })()}
    </div>
  );
}

function ProseSection({ title, body, color, icon }: { title: string; body: string; color: string; icon: JSX.Element }): JSX.Element {
  return (
    <div className="rounded-lg border border-white/8 bg-white/[0.02] p-3">
      <h4 className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color }}>
        {icon} {title}
      </h4>
      <p className="mt-2 text-[12.5px] leading-relaxed text-white/80">{body}</p>
    </div>
  );
}

function NumberedSection({ title, items, color, icon, full }: { title: string; items: string[]; color: string; icon: JSX.Element; full?: boolean }): JSX.Element {
  return (
    <div className={`rounded-lg border border-white/8 bg-white/[0.02] p-3 ${full === true ? "col-span-1 md:col-span-2" : ""}`}>
      <h4 className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color }}>
        {icon} {title}
      </h4>
      <ol className="mt-2 space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2.5 text-[12.5px] leading-relaxed text-white/80">
            <span
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-bold text-[10px]"
              style={{ background: `${color}30`, color, border: `1px solid ${color}66` }}
            >
              {i + 1}
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: number; icon: JSX.Element }): JSX.Element {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white/45">
        {icon} {label}
      </div>
      <div className="mt-1 font-mono text-2xl text-white">{value}</div>
    </div>
  );
}

function BandBar({ strong, developing, needs }: { strong: number; developing: number; needs: number }): JSX.Element {
  const total = strong + developing + needs;
  if (total === 0) return <span className="text-[10px] text-white/30">no completions</span>;
  const sPct = (strong / total) * 100;
  const dPct = (developing / total) * 100;
  const nPct = (needs / total) * 100;
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex h-1.5 w-24 overflow-hidden rounded-full bg-white/5">
        <div style={{ width: `${sPct}%`, background: "#10b981" }} title={`Strong: ${strong}`} />
        <div style={{ width: `${dPct}%`, background: "#f59e0b" }} title={`Developing: ${developing}`} />
        <div style={{ width: `${nPct}%`, background: "#f87272" }} title={`Needs work: ${needs}`} />
      </div>
      <span className="font-mono text-[10px] text-white/55">{strong}/{developing}/{needs}</span>
    </div>
  );
}
